import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../contexts/CartContext';
import { useCurrency } from '../../hooks/useCurrency';
import CartModal from '../../components/feature/CartModal';
import ProductQuickView from '../../components/feature/ProductQuickView';
import { useSEO } from '../../utils/seo';
import { useWishlist } from '../../contexts/WishlistContext';
import { supabase } from '../../lib/supabase';

interface ShopifyProduct {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  image: string;
  variants: Array<{
    id: string;
    title: string;
    price: number;
    availableForSale: boolean;
    quantityAvailable: number;
  }>;
}

const ProductsPage = () => {
  const { t, i18n } = useTranslation();
  const { addToCart, getTotalItems, setIsCartOpen } = useCart();
  const { formatPrice, currency, setCurrency } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<any>(null);
  const isRTL = i18n.language === 'fa';
  const navigate = useNavigate();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch products from Supabase
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('product_inventory')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;

        if (data && data.length > 0) {
          const formattedProducts = data.map((item, index) => {
            // Generate unique image URLs for each product
            const imageUrls: Record<string, string> = {
              'Walnuts': 'https://static.readdy.ai/image/3bccd7c51021826c026374f527c8acfa/0439cbd68fb2875ef65df904153244b3.jpeg',
              'Raw and Salted Almonds': 'https://static.readdy.ai/image/3bccd7c51021826c026374f527c8acfa/fe99c9b01e35270cfa7f88ad2454e2d2.jpeg',
              'Pistachio and almond mixture': 'https://public.readdy.ai/ai/img_res/622a9595ffd327e72fadb3806a63fbe2.jpg',
              'Peanuts': 'https://readdy.ai/api/search-image?query=Roasted%20peanuts%20in%20rustic%20wooden%20bowl%20on%20white%20background%2C%20soft%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=peanutproduct2025final&orientation=squarish',
              'Hazelnuts': 'https://readdy.ai/api/search-image?query=Fresh%20hazelnuts%20in%20ceramic%20bowl%20on%20white%20marble%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20clean%20composition%2C%20professional%20shot&width=800&height=800&seq=hazelnutproduct2025final&orientation=squarish',
              'Chickpeas': 'https://readdy.ai/api/search-image?query=Roasted%20chickpeas%20in%20white%20bowl%20on%20clean%20surface%2C%20soft%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=chickpeaproduct2025final&orientation=squarish',
              'Mango Leaves': 'https://readdy.ai/api/search-image?query=Dried%20mango%20slices%20arranged%20on%20white%20plate%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20clean%20background%2C%20professional%20product%20shot&width=800&height=800&seq=mangoproduct2025final&orientation=squarish',
              'Peaches': 'https://readdy.ai/api/search-image?query=Dried%20peach%20halves%20in%20elegant%20bowl%20on%20white%20surface%2C%20soft%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=peachproduct2025final&orientation=squarish',
              'Plums': 'https://readdy.ai/api/search-image?query=Dried%20plums%20in%20ceramic%20bowl%20on%20white%20marble%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20clean%20composition%2C%20professional%20shot&width=800&height=800&seq=plumproduct2025final&orientation=squarish',
              'Figs': 'https://readdy.ai/api/search-image?query=Premium%20dried%20figs%20on%20white%20plate%2C%20soft%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20elegant%20presentation%2C%20professional%20product%20shot&width=800&height=800&seq=figproduct2025final&orientation=squarish',
              'Bukhara Plums': 'https://readdy.ai/api/search-image?query=Dried%20Bukhara%20plums%20in%20rustic%20bowl%20on%20white%20background%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20shot&width=800&height=800&seq=bukharaproduct2025final&orientation=squarish',
              'Gaz': 'https://readdy.ai/api/search-image?query=Traditional%20Persian%20Gaz%20nougat%20candy%20pieces%20on%20white%20plate%2C%20soft%20lighting%2C%20minimalist%20food%20photography%2C%20elegant%20presentation%2C%20professional%20product%20shot&width=800&height=800&seq=gazproduct2025final&orientation=squarish',
              'Sohan': 'https://readdy.ai/api/search-image?query=Persian%20Sohan%20brittle%20candy%20pieces%20on%20white%20surface%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=sohanproduct2025final&orientation=squarish',
              'Latifeh': 'https://readdy.ai/api/search-image?query=Traditional%20Persian%20Latifeh%20sweets%20arranged%20on%20white%20plate%2C%20soft%20lighting%2C%20minimalist%20food%20photography%2C%20elegant%20presentation%2C%20professional%20shot&width=800&height=800&seq=latifeproduct2025final&orientation=squarish',
              'Pashmak': 'https://readdy.ai/api/search-image?query=Persian%20Pashmak%20cotton%20candy%20in%20glass%20bowl%20on%20white%20background%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=pashmakproduct2025final&orientation=squarish',
              'Lyte': 'https://readdy.ai/api/search-image?query=Persian%20Lyte%20pickled%20vegetables%20in%20glass%20jar%20on%20white%20surface%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=lyteproduct2025final&orientation=squarish',
              'Pearl Garlic': 'https://readdy.ai/api/search-image?query=Pickled%20pearl%20garlic%20cloves%20in%20elegant%20glass%20jar%20on%20white%20background%2C%20soft%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20shot&width=800&height=800&seq=garlicproduct2025final&orientation=squarish',
              'Olives': 'https://readdy.ai/api/search-image?query=Premium%20green%20olives%20in%20ceramic%20bowl%20on%20white%20marble%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=oliveproduct2025final&orientation=squarish',
              'Cultivated Olives': 'https://readdy.ai/api/search-image?query=Cultivated%20black%20olives%20in%20white%20bowl%20on%20clean%20surface%2C%20soft%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=cultivatedoliveproduct2025final&orientation=squarish',
              'Strawberry Jam': 'https://readdy.ai/api/search-image?query=Homemade%20strawberry%20jam%20in%20glass%20jar%20on%20white%20background%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=strawberryjamproduct2025final&orientation=squarish',
              'Carrot Jam': 'https://readdy.ai/api/search-image?query=Traditional%20carrot%20jam%20in%20elegant%20glass%20jar%20on%20white%20surface%2C%20soft%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20shot&width=800&height=800&seq=carrotjamproduct2025final&orientation=squarish',
              'Blackcurrant Jam': 'https://readdy.ai/api/search-image?query=Blackcurrant%20jam%20in%20glass%20jar%20on%20white%20marble%2C%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=blackcurrantjamproduct2025final&orientation=squarish',
              'Honey': 'https://readdy.ai/api/search-image?query=Pure%20golden%20honey%20in%20glass%20jar%20with%20wooden%20dipper%20on%20white%20background%2C%20soft%20natural%20lighting%2C%20minimalist%20food%20photography%2C%20professional%20product%20shot&width=800&height=800&seq=honeyproduct2025final&orientation=squarish',
              'Cashews': 'https://static.readdy.ai/image/3bccd7c51021826c026374f527c8acfa/cdff7e67da5d2888316a752beff3ab3d.jpeg'
            };

            return {
              id: item.product_id,
              name: item.product_name,
              price: parseFloat(item.price) || 0,
              image: imageUrls[item.product_name] || `https://readdy.ai/api/search-image?query=Premium%20$%7Bitem.product_name%7D%20product%20on%20white%20background%2C%20professional%20food%20photography%2C%20minimalist%20style%2C%20natural%20lighting&width=800&height=800&seq=product${index}final2025&orientation=squarish`,
              category: item.category || 'Other',
              description: item.description || `Premium quality ${item.product_name}`,
              stock: item.stock_quantity,
              inStock: item.stock_quantity,
              soldOut: item.stock_quantity === 0,
              rating: 5,
              reviews: Math.floor(Math.random() * 100) + 20,
            };
          });
          setProducts(formattedProducts);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Read category from URL on mount
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  // Update URL when category changes
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const productCategories = [
    {
      title: t('nuts'),
      products: [
        { id: 'walnut-1', name: t('walnuts'), price: 12.99, category: t('nuts'), image: 'https://readdy.ai/api/search-image?query=Premium%20whole%20walnuts%20scattered%20on%20rustic%20wooden%20table%20natural%20lighting%20showcasing%20rich%20brown%20walnut%20shells%20and%20golden%20kernels%20healthy%20organic%20food%20photography%20minimalist%20composition%20high%20quality%20commercial%20product%20shot&width=400&height=400&seq=walnutprod2025a&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'almond-1', name: t('almonds'), price: 15.99, category: t('nuts'), image: 'https://readdy.ai/api/search-image?query=Raw%20natural%20almonds%20in%20ceramic%20bowl%20on%20white%20marble%20surface%20professional%20food%20photography%20showcasing%20almond%20texture%20and%20natural%20brown%20color%20elegant%20minimalist%20style%20high%20quality%20commercial%20shot&width=400&height=400&seq=almondprod2025b&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'pistachio-1', name: t('pistachios'), price: 18.99, category: t('nuts'), image: 'https://static.readdy.ai/image/3bccd7c51021826c026374f527c8acfa/9f88bbdfec03b9ed451d7dd83d4c6495.jpeg', inStock: 0, soldOut: true },
        { id: 'peanut-1', name: t('peanuts'), price: 8.99, category: t('nuts'), image: 'https://readdy.ai/api/search-image?query=Fresh%20roasted%20peanuts%20in%20rustic%20wooden%20bowl%20on%20natural%20burlap%20background%20organic%20food%20photography%20style%20warm%20earth%20tones%20showcasing%20peanut%20shells%20and%20texture%20high%20quality%20commercial%20shot&width=400&height=400&seq=peanutprod2025d&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'hazelnut-1', name: t('hazelnuts'), price: 16.99, category: t('nuts'), image: 'https://readdy.ai/api/search-image?query=Premium%20whole%20hazelnuts%20scattered%20naturally%20on%20clean%20white%20marble%20surface%20with%20soft%20shadows%20elegant%20food%20photography%20minimalist%20composition%20warm%20natural%20lighting%20high%20quality%20commercial%20product%20shot&width=400&height=400&seq=hazelnutprod2025e&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'chickpea-1', name: t('chickpeas'), price: 9.99, category: t('nuts'), image: 'https://readdy.ai/api/search-image?query=Roasted%20golden%20chickpeas%20in%20ceramic%20bowl%20healthy%20snack%20photography%20natural%20lighting%20rustic%20style%20showcasing%20golden%20brown%20chickpeas%20texture%20high%20quality%20commercial%20shot&width=400&height=400&seq=chickpeaprod2025f&orientation=squarish', inStock: 0, soldOut: true }
      ]
    },
    {
      title: t('driedFruits'),
      products: [
        { id: 'mango-1', name: t('mangoLeaves'), price: 14.99, category: t('driedFruits'), image: 'https://readdy.ai/api/search-image?query=Dried%20mango%20slices%20arranged%20beautifully%20on%20white%20ceramic%20plate%20vibrant%20orange%20golden%20colors%20professional%20food%20photography%20clean%20presentation%20showcasing%20natural%20fruit%20texture%20high%20quality%20commercial%20shot&width=400&height=400&seq=mangoprod2025g&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'peach-1', name: t('peaches'), price: 13.99, category: t('driedFruits'), image: 'https://readdy.ai/api/search-image?query=Dried%20peach%20halves%20in%20glass%20jar%20with%20natural%20lighting%20warm%20golden%20tones%20artisanal%20food%20photography%20style%20showcasing%20peachy%20orange%20color%20high%20quality%20commercial%20shot&width=400&height=400&seq=peachprod2025h&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'plum-1', name: t('plums'), price: 12.99, category: t('driedFruits'), image: 'https://readdy.ai/api/search-image?query=Dried%20plums%20in%20wooden%20bowl%20on%20rustic%20background%20deep%20purple%20colors%20natural%20food%20photography%20organic%20style%20showcasing%20rich%20plum%20texture%20high%20quality%20commercial%20shot&width=400&height=400&seq=plumprod2025i&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'fig-1', name: t('figs'), price: 19.99, category: t('driedFruits'), image: 'https://readdy.ai/api/search-image?query=Premium%20dried%20figs%20arranged%20on%20marble%20surface%20rich%20brown%20colors%20luxury%20food%20photography%20elegant%20presentation%20showcasing%20fig%20texture%20and%20natural%20sweetness%20high%20quality%20commercial%20shot&width=400&height=400&seq=figprod2025j&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'bukharaplum-1', name: t('bukharaPlums'), price: 15.99, category: t('driedFruits'), image: 'https://readdy.ai/api/search-image?query=Bukhara%20dried%20plums%20in%20traditional%20Persian%20bowl%20authentic%20Middle%20Eastern%20style%20warm%20lighting%20cultural%20food%20photography%20showcasing%20dark%20plum%20color%20high%20quality%20commercial%20shot&width=400&height=400&seq=bukharaplumprod2025k&orientation=squarish', inStock: 0, soldOut: true }
      ]
    },
    {
      title: t('sweets'),
      products: [
        { id: 'gaz-1', name: t('gaz'), price: 22.99, category: t('sweets'), image: 'https://readdy.ai/api/search-image?query=Traditional%20Persian%20gaz%20nougat%20pieces%20on%20ornate%20silver%20tray%20authentic%20Middle%20Eastern%20confectionery%20warm%20golden%20lighting%20cultural%20sweets%20photography%20showcasing%20white%20nougat%20texture%20high%20quality%20commercial%20shot&width=400&height=400&seq=gazprod2025l&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'sohan-1', name: t('sohan'), price: 24.99, category: t('sweets'), image: 'https://readdy.ai/api/search-image?query=Golden%20sohan%20brittle%20pieces%20with%20pistachios%20on%20decorative%20plate%20traditional%20Persian%20sweets%20warm%20amber%20lighting%20luxury%20confectionery%20photography%20showcasing%20golden%20caramel%20color%20high%20quality%20commercial%20shot&width=400&height=400&seq=sohanprod2025m&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'latifeh-1', name: t('latifeh'), price: 18.99, category: t('sweets'), image: 'https://readdy.ai/api/search-image?query=Latifeh%20Persian%20cookies%20arranged%20on%20vintage%20plate%20traditional%20Middle%20Eastern%20pastries%20warm%20lighting%20authentic%20cultural%20sweets%20showcasing%20cookie%20texture%20high%20quality%20commercial%20shot&width=400&height=400&seq=latifehprod2025n&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'pashmak-1', name: t('pashmak'), price: 16.99, category: t('sweets'), image: 'https://readdy.ai/api/search-image?query=Delicate%20pashmak%20cotton%20candy%20in%20clear%20container%20white%20fluffy%20texture%20Persian%20fairy%20floss%20elegant%20food%20photography%20showcasing%20cotton%20candy%20strands%20high%20quality%20commercial%20shot&width=400&height=400&seq=pashmakprod2025o&orientation=squarish', inStock: 0, soldOut: true }
      ]
    },
    {
      title: t('pickles'),
      products: [
        { id: 'lyte-1', name: t('lyte'), price: 11.99, category: t('pickles'), image: 'https://readdy.ai/api/search-image?query=Various%20pickled%20vegetables%20lyte%20in%20glass%20jars%20colorful%20preserved%20foods%20rustic%20kitchen%20photography%20artisanal%20preserves%20style%20showcasing%20vibrant%20vegetable%20colors%20high%20quality%20commercial%20shot&width=400&height=400&seq=lyteprod2025p&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'garlic-1', name: t('pearlGarlic'), price: 13.99, category: t('pickles'), image: 'https://readdy.ai/api/search-image?query=Pearl%20garlic%20cloves%20in%20brine%20in%20glass%20jar%20white%20pickled%20garlic%20clean%20food%20photography%20minimalist%20presentation%20showcasing%20white%20garlic%20cloves%20high%20quality%20commercial%20shot&width=400&height=400&seq=garlicprod2025q&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'olive-1', name: t('olives'), price: 14.99, category: t('pickles'), image: 'https://readdy.ai/api/search-image?query=Mixed%20olives%20in%20ceramic%20bowl%20with%20herbs%20Mediterranean%20style%20natural%20lighting%20rustic%20food%20photography%20showcasing%20green%20and%20black%20olives%20high%20quality%20commercial%20shot&width=400&height=400&seq=oliveprod2025r&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'cultivatedolive-1', name: t('cultivatedOlives'), price: 16.99, category: t('pickles'), image: 'https://readdy.ai/api/search-image?query=Premium%20cultivated%20olives%20in%20elegant%20glass%20container%20gourmet%20food%20photography%20sophisticated%20presentation%20natural%20lighting%20showcasing%20premium%20olive%20quality%20high%20quality%20commercial%20shot&width=400&height=400&seq=cultivatedoliveprod2025s&orientation=squarish', inStock: 0, soldOut: true }
      ]
    },
    {
      title: t('jams'),
      products: [
        { id: 'strawberryjam-1', name: t('strawberryJam'), price: 9.99, category: t('jams'), image: 'https://readdy.ai/api/search-image?query=Homemade%20strawberry%20jam%20in%20glass%20jar%20with%20fresh%20strawberries%20around%20bright%20red%20colors%20rustic%20kitchen%20photography%20artisanal%20preserves%20showcasing%20red%20jam%20texture%20high%20quality%20commercial%20shot&width=400&height=400&seq=strawberryjamprod2025t&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'carrotjam-1', name: t('carrotJam'), price: 8.99, category: t('jams'), image: 'https://readdy.ai/api/search-image?query=Unique%20carrot%20jam%20in%20mason%20jar%20with%20fresh%20carrots%20orange%20golden%20color%20creative%20food%20photography%20healthy%20preserves%20style%20showcasing%20orange%20jam%20high%20quality%20commercial%20shot&width=400&height=400&seq=carrotjamprod2025u&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'blackcurrantjam-1', name: t('blackcurrantJam'), price: 12.99, category: t('jams'), image: 'https://readdy.ai/api/search-image?query=Dark%20blackcurrant%20jam%20in%20vintage%20jar%20with%20fresh%20berries%20deep%20purple%20colors%20rustic%20food%20photography%20artisanal%20style%20showcasing%20dark%20purple%20jam%20high%20quality%20commercial%20shot&width=400&height=400&seq=blackcurrantjamprod2025v&orientation=squarish', inStock: 0, soldOut: true },
        { id: 'honey-1', name: t('honey'), price: 19.99, category: t('jams'), image: 'https://readdy.ai/api/search-image?query=Pure%20golden%20honey%20in%20glass%20jar%20with%20wooden%20dipper%20warm%20amber%20colors%20natural%20food%20photography%20organic%20honey%20presentation%20showcasing%20golden%20honey%20texture%20high%20quality%20commercial%20shot&width=400&height=400&seq=honeyprod2025w&orientation=squarish', inStock: 0, soldOut: true }
      ]
    }
  ];

  // Flatten all products and merge with inventory data
  const mockProducts = productCategories.flatMap(cat => cat.products);
  
  // Merge mock products with real inventory data from Supabase
  const allProducts = mockProducts.map(mockProduct => {
    const inventoryItem = products.find(inv => {
      // Match by name since IDs might be different
      return inv.name.toLowerCase() === mockProduct.name.toLowerCase();
    });
    
    if (inventoryItem) {
      // Use inventory data if available
      return {
        ...mockProduct,
        id: inventoryItem.id,
        name: inventoryItem.name,
        price: inventoryItem.price,
        image: inventoryItem.image,
        category: inventoryItem.category,
        inStock: inventoryItem.inStock,
        soldOut: inventoryItem.soldOut,
        description: inventoryItem.description || mockProduct.description,
        rating: inventoryItem.rating || mockProduct.rating,
        reviews: inventoryItem.reviews || mockProduct.reviews,
      };
    }
    
    return mockProduct;
  });

  // Add products that are only in inventory (not in mock data)
  products.forEach(inventoryItem => {
    const existsInMock = mockProducts.some(mp => 
      mp.name.toLowerCase() === inventoryItem.name.toLowerCase()
    );
    if (!existsInMock) {
      allProducts.push({
        id: inventoryItem.id,
        name: inventoryItem.name,
        price: inventoryItem.price,
        image: inventoryItem.image,
        category: inventoryItem.category,
        inStock: inventoryItem.inStock,
        soldOut: inventoryItem.soldOut,
        description: inventoryItem.description,
        rating: inventoryItem.rating || 5,
        reviews: inventoryItem.reviews || 0,
      });
    }
  });

  // Get all unique categories with proper structure
  const categories = [
    { id: 'all', name: 'All Products', icon: 'ri-apps-line' },
    { id: t('nuts'), name: t('nuts'), icon: 'ri-plant-line' },
    { id: t('driedFruits'), name: t('driedFruits'), icon: 'ri-leaf-line' },
    { id: t('sweets'), name: t('sweets'), icon: 'ri-cake-3-line' },
    { id: t('pickles'), name: t('pickles'), icon: 'ri-flask-line' },
    { id: t('jams'), name: t('jams'), icon: 'ri-drop-line' }
  ];

  // Filter products based on search and category
  const filteredProducts = allProducts.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Schema.org JSON-LD for products page
  const productsSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products#webpage`,
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products`,
        name: 'Premium Food Products - Loveish Group',
        description: 'Browse our selection of premium food products including dried fruits, nuts, traditional sweets, pickles, and artisanal jams.',
        isPartOf: {
          '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}#website`
        },
        breadcrumb: {
          '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products#breadcrumb`
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: import.meta.env.VITE_SITE_URL || 'https://example.com'
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Products',
            item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products`
          }
        ]
      },
      {
        '@type': 'ItemList',
        '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products#itemlist`,
        itemListElement: filteredProducts.map((product, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            name: product.name,
            image: product.image,
            description: product.description || `Premium ${product.name} from Loveish Group`,
            offers: {
              '@type': 'Offer',
              price: product.price,
              priceCurrency: 'USD',
              availability: 'https://schema.org/InStock',
              url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products`,
              seller: {
                '@type': 'Organization',
                name: 'Loveish Group'
              }
            },
            brand: {
              '@type': 'Brand',
              name: 'Loveish Group'
            }
          }
        }))
      }
    ]
  };

  // SEO Configuration
  useSEO({
    title: 'Premium Food Products - Loveish Group',
    description: 'Browse our selection of premium food products including dried fruits, nuts, traditional sweets, pickles, and artisanal jams.',
    keywords: 'food products online, buy food, premium items, dried fruit, nuts, sweets, pickles, jams',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products`,
    ogType: 'website',
    schema: productsSchema
  });

  const handleQuickView = (product: typeof allProducts[0]) => {
    setQuickViewProduct(product);
    setIsQuickViewOpen(true);
  };

  const handleCloseQuickView = () => {
    setIsQuickViewOpen(false);
    setTimeout(() => setQuickViewProduct(null), 300);
  };

  const handleBuyNow = (product: { id: string; name: string; price: number; category?: string; image: string }) => {
    addToCart(product);
    setIsCartOpen(true);
  };

  const handleWishlistToggle = (product: any) => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        category: product.category,
        description: product.description
      });
    }
  };

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
    { code: 'IRR', symbol: '﷼', name: 'Iranian Rial', rate: 42000 },
    { code: 'AMD', symbol: '֏', name: 'Armenian Dram', rate: 400 },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble', rate: 90 }
  ];

  const currentCurrency = currencies.find(c => c.code === currency.code) || currencies[0];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-emerald-50/30">
      <Header />
      
      <main className="pt-16 sm:pt-20 md:pt-24 pb-12 sm:pb-16">
        {/* Loading State */}
        {loading && (
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 font-semibold">Loading products...</p>
            </div>
          </div>
        )}

        {/* Filters Section */}
        <section className="relative py-8 md:py-12 px-4 md:px-6 overflow-hidden">
          {/* Modern Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle, #10b981 1px, transparent 1px)',
              backgroundSize: '30px 30px',
            }}></div>
          </div>

          {/* Floating Orbs */}
          <div className="absolute top-10 md:top-20 right-0 md:-right-20 w-48 h-48 md:w-96 md:h-96 bg-gradient-to-br from-emerald-200/40 to-teal-200/40 rounded-full blur-3xl animate-float"></div>
          <div className="absolute bottom-10 md:bottom-20 left-0 md:-left-20 w-48 h-48 md:w-96 md:h-96 bg-gradient-to-br from-teal-200/40 to-emerald-200/40 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>

          <div className="max-w-7xl mx-auto relative z-10">
            {/* Filters Section */}
            <div className="relative z-10 bg-gradient-to-br from-white via-white to-emerald-50/30 backdrop-blur-xl shadow-2xl rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-8 mb-6 md:mb-8 lg:mb-12 border-2 border-gray-100/50">
              {/* Search Bar */}
              <div className="mb-4 md:mb-6">
                <div className="relative max-w-3xl mx-auto group">
                  {/* Animated gradient border effect */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 blur-sm transition-all duration-500 animate-gradient-x"></div>
                  
                  <div className="relative">
                    {/* Search Icon Badge */}
                    <div className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-emerald-500/50 group-hover:scale-110 transition-all duration-300 z-10">
                      <i className="ri-search-line text-white text-lg md:text-2xl group-hover:rotate-12 transition-transform duration-300"></i>
                    </div>
                    
                    {/* Search Input */}
                    <input
                      type="text"
                      placeholder={t('searchProducts')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="relative w-full px-4 md:px-6 py-4 md:py-5 lg:py-6 pl-16 md:pl-20 lg:pl-24 pr-12 md:pr-14 bg-white/90 backdrop-blur-xl border-2 border-gray-200 rounded-xl md:rounded-2xl focus:outline-none focus:border-transparent focus:ring-4 focus:ring-emerald-500/30 transition-all duration-300 text-sm md:text-base lg:text-lg shadow-xl hover:shadow-2xl font-medium placeholder:text-gray-400 hover:bg-white hover:border-emerald-300 group-hover:border-emerald-400"
                      style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)'
                      }}
                    />
                    
                    {/* Clear Button */}
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-lg md:rounded-xl flex items-center justify-center transition-all duration-300 cursor-pointer touch-manipulation shadow-lg hover:shadow-red-500/50 hover:scale-110 hover:rotate-90 group/clear z-10"
                        aria-label="Clear search"
                      >
                        <i className="ri-close-line text-base md:text-xl font-bold group-hover/clear:rotate-90 transition-transform duration-300"></i>
                      </button>
                    )}
                    
                    {/* Animated search indicator */}
                    {searchQuery && (
                      <div className="absolute left-16 md:left-24 top-1/2 -translate-y-1/2 flex items-center gap-1 animate-pulse">
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Search Tips */}
                <div className="mt-3 md:mt-4 text-center">
                  <p className="text-xs md:text-sm text-gray-500 font-medium flex items-center justify-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full">
                      <i className="ri-lightbulb-flash-line text-sm"></i>
                      <span className="font-semibold">Tip:</span>
                    </span>
                    <span>Try searching by name, category, or description</span>
                  </p>
                </div>
              </div>

              {/* Currency Selector & Category Filters Row */}
              <div className="flex flex-col gap-4 md:gap-6">
                {/* Currency Selector */}
                <div className="relative w-full flex justify-center">
                  <button
                    onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                    className="w-full md:w-auto px-4 md:px-6 py-3 md:py-4 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 hover:from-emerald-700 hover:via-emerald-600 hover:to-teal-700 text-white rounded-xl md:rounded-2xl font-bold transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 md:gap-3 shadow-xl hover:shadow-2xl hover:scale-105 whitespace-nowrap group touch-manipulation"
                  >
                    <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 backdrop-blur-sm rounded-lg md:rounded-xl flex items-center justify-center">
                      <i className="ri-money-dollar-circle-line text-xl md:text-2xl"></i>
                    </div>
                    <span className="text-sm md:text-base lg:text-lg">Currency: {currentCurrency.code}</span>
                    <i className={`ri-arrow-down-s-line text-lg md:text-xl transition-transform duration-300 ${showCurrencyDropdown ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* Currency Dropdown */}
                  {showCurrencyDropdown && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-full md:w-80 bg-white rounded-xl md:rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                      {/* Dropdown Header */}
                      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 md:px-6 py-3 md:py-4 border-b-2 border-emerald-700">
                        <div className="flex items-center gap-2 md:gap-3">
                          <div className="w-8 h-8 md:w-10 md:h-10 bg-white/20 backdrop-blur-sm rounded-lg md:rounded-xl flex items-center justify-center">
                            <i className="ri-exchange-dollar-line text-white text-lg md:text-xl"></i>
                          </div>
                          <h3 className="text-white font-bold text-base md:text-lg">Select Currency</h3>
                        </div>
                      </div>
                      
                      {/* Currency Options */}
                      <div className="max-h-80 md:max-h-96 overflow-y-auto">
                        {currencies.map((curr) => (
                          <button
                            key={curr.code}
                            onClick={() => {
                              setCurrency(curr.code);
                              setShowCurrencyDropdown(false);
                            }}
                            className={`w-full px-4 md:px-6 py-3 md:py-4 text-left hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 transition-all cursor-pointer flex items-center justify-between group border-b border-gray-100 last:border-b-0 touch-manipulation ${
                              currency.code === curr.code ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-l-emerald-600' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3 md:gap-4">
                              <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center text-xl md:text-2xl font-bold transition-all ${
                              currency.code === curr.code 
                                ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg' 
                                : 'bg-gray-100 text-gray-600 group-hover:bg-gradient-to-br group-hover:from-emerald-500 group-hover:to-teal-600 group-hover:text-white'
                              }`}>
                                {curr.symbol}
                              </div>
                              <div>
                                <div className={`font-bold text-sm md:text-base ${currency.code === curr.code ? 'text-emerald-700' : 'text-gray-900'}`}>
                                  {curr.code}
                                </div>
                                <div className="text-xs md:text-sm text-gray-500 font-medium">
                                  {curr.name}
                                </div>
                              </div>
                            </div>
                            {currency.code === curr.code && (
                              <div className="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-lg">
                                <i className="ri-check-line text-white text-base md:text-lg"></i>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Category Filters */}
                <div className="w-full overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
                  <div className="flex items-center gap-2 md:gap-3 min-w-max md:flex-wrap md:min-w-0">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryChange(cat.id)}
                        className={`px-4 md:px-6 py-3 md:py-4 rounded-xl md:rounded-2xl font-bold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2 md:gap-3 shadow-lg hover:shadow-xl hover:scale-105 touch-manipulation text-xs md:text-sm lg:text-base ${
                          selectedCategory === cat.id
                            ? 'bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 text-white shadow-emerald-500/40'
                            : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 hover:text-emerald-700 border-2 border-gray-200'
                        }`}
                      >
                        <div className={`w-6 h-6 md:w-8 md:h-8 rounded-lg md:rounded-xl flex items-center justify-center ${
                          selectedCategory === cat.id 
                            ? 'bg-white/20 backdrop-blur-sm' 
                            : 'bg-gradient-to-br from-emerald-500 to-teal-600'
                        }`}>
                          <i className={`${cat.icon} text-sm md:text-lg ${selectedCategory === cat.id ? 'text-white' : 'text-white'}`}></i>
                        </div>
                        <span>{cat.name}</span>
                        {selectedCategory === cat.id && (
                          <div className="w-5 h-5 md:w-6 md:h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs md:text-sm"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Active Filters Display */}
              {(searchQuery || selectedCategory !== 'all') && (
                <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t-2 border-gray-100">
                  <div className="flex flex-wrap items-center gap-2 md:gap-3">
                    <span className="text-xs md:text-sm font-bold text-gray-600 flex items-center gap-2">
                      <i className="ri-filter-3-line text-emerald-600"></i>
                      Active Filters:
                    </span>
                    {searchQuery && (
                      <div className="px-3 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 rounded-lg md:rounded-xl font-semibold text-xs md:text-sm flex items-center gap-2 shadow-md">
                        <i className="ri-search-line text-sm md:text-base"></i>
                        <span className="max-w-[120px] md:max-w-none truncate">Search: "{searchQuery}"</span>
                        <button
                          onClick={() => setSearchQuery('')}
                          className="w-4 h-4 md:w-5 md:h-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex items-center justify-center transition-all cursor-pointer ml-1 flex-shrink-0 touch-manipulation"
                        >
                          <i className="ri-close-line text-[10px] md:text-xs"></i>
                        </button>
                      </div>
                    )}
                    {selectedCategory !== 'all' && (
                      <div className="px-3 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 rounded-lg md:rounded-xl font-semibold text-xs md:text-sm flex items-center gap-2 shadow-md">
                        <i className={`${categories.find(c => c.id === selectedCategory)?.icon} text-sm md:text-base`}></i>
                        <span>{categories.find(c => c.id === selectedCategory)?.name}</span>
                        <button
                          onClick={() => handleCategoryChange('all')}
                          className="w-4 h-4 md:w-5 md:h-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full flex items-center justify-center transition-all cursor-pointer ml-1 flex-shrink-0 touch-manipulation"
                        >
                          <i className="ri-close-line text-[10px] md:text-xs"></i>
                        </button>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        handleCategoryChange('all');
                      }}
                      className="px-3 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg md:rounded-xl font-semibold text-xs md:text-sm flex items-center gap-2 shadow-md hover:shadow-lg transition-all cursor-pointer whitespace-nowrap touch-manipulation"
                    >
                      <i className="ri-close-circle-line text-sm md:text-base"></i>
                      Clear All
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Products Grid */}
            <section className="relative py-8 md:py-12 lg:py-16 xl:py-20 px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer hover:-translate-y-2"
                      onClick={() => handleQuickView(product)}
                    >
                      {/* Image Container */}
                      <div className="relative w-full h-64 sm:h-72 md:h-80 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                        />
                        
                        {/* Stock Badge - Top Left */}
                        <div className="absolute top-4 left-4 z-20">
                          <div className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-lg backdrop-blur-sm flex items-center gap-1.5 ${
                            product.inStock === 0
                              ? 'bg-red-500/90 text-white'
                              : product.inStock <= 5
                              ? 'bg-amber-500/90 text-white animate-pulse'
                              : 'bg-emerald-500/90 text-white'
                          }`}>
                            <i className={`${
                              product.inStock === 0
                                ? 'ri-close-circle-fill'
                                : product.inStock <= 5
                                ? 'ri-alert-fill'
                                : 'ri-checkbox-circle-fill'
                            } text-sm`}></i>
                            <span>
                              {product.inStock === 0 
                                ? 'Out of Stock' 
                                : product.inStock <= 5
                                ? `Only ${product.inStock} left!`
                                : `${product.inStock} in stock`
                              }
                            </span>
                          </div>
                        </div>
                        
                        {/* Wishlist Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWishlistToggle(product);
                          }}
                          className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors shadow-lg z-20 touch-manipulation"
                        >
                          <i
                            className={`${
                              isInWishlist(product.id)
                                ? 'ri-heart-fill text-red-500'
                                : 'ri-heart-line text-gray-600'
                            } text-xl`}
                          ></i>
                        </button>
                      </div>

                      {/* Product Info */}
                      <div className="p-4 md:p-5 lg:p-6 space-y-3">
                        {/* Category Badge */}
                        {product.category && (
                          <div className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-lg">
                            {product.category}
                          </div>
                        )}

                        {/* Product Name */}
                        <h3 className="font-bold text-gray-900 text-base md:text-lg group-hover:text-emerald-600 transition-colors duration-300 line-clamp-2 min-h-[3rem]">
                          {product.name}
                        </h3>

                        {/* Price & Sold Out Badge */}
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-2xl md:text-3xl font-bold text-emerald-600">
                            {formatPrice(product.price)}
                          </div>
                          
                          {/* Sold Out Badge - Modern Design */}
                          {product.soldOut && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-full shadow-lg animate-pulse">
                              <i className="ri-close-circle-fill text-lg"></i>
                              <span className="text-xs font-bold uppercase tracking-wide">
                                {t('soldOut')}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <i
                                key={i}
                                className={`${
                                  i < Math.floor(product.rating || 5) ? 'ri-star-fill' : 'ri-star-line'
                                } text-amber-500 text-sm`}
                              ></i>
                            ))}
                          </div>
                          <span className="text-sm text-gray-600 font-semibold">
                            {product.rating || 5}
                          </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2 pt-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuickView(product);
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-emerald-600 rounded-xl font-semibold hover:bg-emerald-600 hover:text-white transition-all duration-300 shadow-lg border-2 border-emerald-600 whitespace-nowrap cursor-pointer text-sm touch-manipulation"
                          >
                            <i className="ri-eye-line text-lg"></i>
                            <span>Quick View</span>
                          </button>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!product.soldOut && product.inStock > 0) {
                                  handleBuyNow(product);
                                }
                              }}
                              disabled={product.soldOut || product.inStock === 0}
                              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer text-sm touch-manipulation ${
                                product.soldOut || product.inStock === 0
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white hover:shadow-emerald-500/50 hover:scale-105'
                              }`}
                            >
                              <i className={`${product.soldOut || product.inStock === 0 ? 'ri-close-circle-line' : 'ri-shopping-cart-line'} text-lg`}></i>
                              <span className="hidden sm:inline">{product.soldOut || product.inStock === 0 ? t('outOfStock') : t('addToCart')}</span>
                              <span className="sm:hidden">{product.soldOut || product.inStock === 0 ? 'Out' : 'Add'}</span>
                            </button>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWishlistToggle(product);
                              }}
                              className={`w-12 h-12 flex items-center justify-center rounded-xl font-bold transition-all duration-300 cursor-pointer shadow-lg hover:scale-110 touch-manipulation ${
                                isInWishlist(product.id)
                                  ? 'bg-gradient-to-r from-rose-500 to-pink-600 text-white hover:from-rose-600 hover:to-pink-700'
                                  : 'bg-white border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50'
                              }`}
                              aria-label={isInWishlist(product.id) ? 'Remove from Wishlist' : 'Add to Wishlist'}
                            >
                              <i className={`${isInWishlist(product.id) ? 'ri-heart-fill' : 'ri-heart-line'} text-xl`}></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* No Results */}
                {filteredProducts.length === 0 && (
                  <div className="text-center py-12 md:py-16 lg:py-20 animate-fade-in-up px-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 mx-auto mb-4 md:mb-6 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center">
                      <i className="ri-search-line text-3xl sm:text-4xl md:text-5xl text-emerald-600"></i>
                    </div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
                      No products found
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-4 md:mb-6 px-4">
                      Try adjusting your search or filter to find what you're looking for
                    </p>
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        handleCategoryChange('all');
                      }}
                      className="px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-lg md:rounded-xl lg:rounded-2xl font-semibold transition-all duration-300 shadow-lg hover:shadow-emerald-500/50 hover:scale-105 cursor-pointer inline-flex items-center gap-2 whitespace-nowrap text-xs sm:text-sm md:text-base touch-manipulation"
                    >
                      <i className="ri-refresh-line text-base sm:text-lg md:text-xl"></i>
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            </section>
          </div>
        </section>
      </main>

      <Footer />

      <ProductQuickView
        product={quickViewProduct}
        isOpen={isQuickViewOpen}
        onClose={handleCloseQuickView}
        onAddToCart={(product) => {
          addToCart(product);
          setIsCartOpen(true);
        }}
      />

      <CartModal />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-full shadow-2xl hover:shadow-emerald-500/50 flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer z-40 animate-bounce-in touch-manipulation"
          aria-label="Scroll to top"
        >
          <i className="ri-arrow-up-line text-2xl"></i>
        </button>
      )}
    </div>
  );
};

export default ProductsPage;
